import React, { useState, useEffect, useRef, useCallback} from 'react';
import {
  Box,
  Typography,
  Paper,
  CircularProgress,
  TextField
} from '@mui/material';
import { ValidatedIcon, UnvalidatedIcon } from '../icons/ValidationIcons';
import ActionButtons from './ActionButtons';
import useInlineComments from '../../hooks/useInlineComments';
import CommentThreadPanel from '../comments/CommentThreadPanel';
import TextSelectionPopup from '../comments/TextSelectionPopup';
import useFullScreen from '../../hooks/useFullScreen';
import MarkdownWithMermaid from '../common/MarkdownWithMermaid';
import useValidation from '../../hooks/useValidation';
import TaskApprovalBar from './TaskApprovalBar';
import mermaid from 'mermaid';
import SectionHeader from '../atoms/Header';
import TopBarButtons from '../atoms/TopBarButtons';
import LoadMoreButton from '../atoms/LoadMore';
import ConfirmationDialog from '../common/ConfirmationDialog';
import useDynamicSections from '../../hooks/useDynamicSections';
import useSectionCRUD from '../../hooks/useSectionCRUD';
import useContentActions from '../../hooks/useContentActions';
import useNotification from '../../hooks/useNotification';
import EmptyContentPlaceholder from '../atoms/EmptyContentPlaceholder';
import { Accordion, ScrollableBox, EditableTextField } from '../atoms';
import useExplain from '../../hooks/useExplain';
import ExplanationModal from '../atoms/ExplanationModal';
import useImprove from '../../hooks/useImprove';
import InlineImproveInput from '../common/InlineImproveInput';
import ImprovementModal from '../atoms/ImprovementModal';
import { parseCICDPipelineSections } from '../../utils/markdownSectionParser';
import useCICDPipelineVersions from '../../hooks/useCICDPipelineVersions';
import VersionSelector from '../atoms/VersionSelector';
import AddSectionModal from '../atoms/AddSectionModal';
import conversationService from '../../services/conversationService';
import MarkdownRenderer from '../common/MarkdownRenderer';
import LoadingBox from '../atoms/LoadingBox';
import useParsedSections from '../../hooks/useParsedSections';

// Initialize mermaid config
try {
  mermaid.initialize({
    startOnLoad: false,
    theme: 'default',
    securityLevel: 'loose',
    flowchart: { useMaxWidth: false, htmlLabels: true },
    sequence: { diagramMarginX: 50, diagramMarginY: 10 },
    classDiagram: { useMaxWidth: false },
    er: { useMaxWidth: false },
    gantt: { useMaxWidth: false }
  });
} catch (error) {
  console.error('Error initializing mermaid:', error);
}


/**
 * A split-view layout component that shows conversation on the left
 * and dynamically rendered CI/CD pipeline content on the right with accordion sections
 */
const SplitViewCICDPipeline = ({ conversation, children, isStreaming, streamingContent, projectId, onConversationUpdate }) => {
  const [sidebarCollapsed, setSidebarCollapsed] = useState(false);
  const [zoomLevel, setZoomLevel] = useState(1.1); // Default zoom level for mermaid diagrams
  const { showNotification } = useNotification();
  const pipelinePanelRef = useRef(null);
  const isLocalUpdateRef = useRef(false); // Track if we're doing a local update

  // Use version management for CI/CD pipeline
  const {
    versions,
    currentVersion,
    currentVersionIndex,
    setCurrentVersionIndex,
    hasVersions,
    isLatestVersion,
    updateCurrentVersion
  } = useCICDPipelineVersions(conversation, isStreaming, streamingContent, isLocalUpdateRef);

  // useParsedSections hook — single source of truth for section data
  const {
    sections: parsedSections,
    setSections: setParsedSections,
    groupedSections,
    isLoading: isSectionsLoading,
    contentRef,
    refresh: refreshSections,
    markLocalEdit,
  } = useParsedSections({ conversation, isStreaming, streamingContent });

  const [expandedSections, setExpandedSections] = useState({});

  // Section highlight from search navigation (?section=sectionId)
  const useSectionHighlight = require('../../hooks/useSectionHighlight').default;
  const { highlightRef, shouldHighlight } = useSectionHighlight(expandedSections, setExpandedSections);
  const [visibleSections, setVisibleSections] = useState(10);
  const [showAddSectionModal, setShowAddSectionModal] = useState(false);

  // Track previous version index to detect actual version changes
  const prevVersionIndexRef = useRef(currentVersionIndex);

  // Check if this is a CI/CD pipeline conversation
  const isCICDPipelineConversation = conversation?.taskId === 'cicd-pipeline' ||
    (conversation?.messages?.some(msg =>
      msg.role === 'assistant' &&
      (msg.content?.includes('## Build Stage') ||
       msg.content?.includes('## Test Stage') ||
       msg.content?.includes('## Deploy Stage') ||
       msg.content?.includes('CI/CD Pipeline'))
    ));

  const hasContent = parsedSections.length > 0;

  // Version switching: reset visible sections when switching versions
  useEffect(() => {
    if (currentVersion) {
      if (prevVersionIndexRef.current !== currentVersionIndex) {
        setVisibleSections(10);
        prevVersionIndexRef.current = currentVersionIndex;
      }
    }
  }, [currentVersion, currentVersionIndex]);

  const handleSectionToggle = (sectionId) => {
    setExpandedSections(prev => ({
      ...prev,
      [sectionId]: !prev[sectionId]
    }));
  };

  const handleLoadMore = () => {
    setVisibleSections(prev => Math.min(prev + 10, parsedSections.length));
  };

  const handleShowAll = () => {
    setVisibleSections(parsedSections.length);
  };

  // Helper function to check if content has Mermaid diagram
  const hasMermaidDiagram = (content) => {
    return content && content.includes('```mermaid');
  };

  // Zoom controls for mermaid diagrams
  const handleZoomIn = () => {
    setZoomLevel(prev => Math.min(prev + 0.25, 2.0));
  };

  const handleZoomOut = () => {
    setZoomLevel(prev => Math.max(prev - 0.25, 0.25));
  };

  // Use CRUD operations hook with version support
  const {
    editingSection,
    editedContent,
    editedTitle,
    isSaving,
    deleteConfirmation,
    setEditedContent,
    setEditedTitle,
    setDeleteConfirmation,
    handleStartEdit,
    handleCancelEdit,
    handleSaveEdit,
    handleDeleteSection,
    handleDeleteSectionConfirmed,
    handleImproveSection,
    handleExplainSection: handleExplainSectionBase,
    handleCopySection
  } = useSectionCRUD(
    conversation,
    parsedSections,
    setParsedSections,
    setExpandedSections,
    contentRef,
    updateCurrentVersion,
    currentVersion,
    isLocalUpdateRef,
    null, // onConversationUpdate
    markLocalEdit
  );

  // Override explain handler to use the useExplain hook
  const handleExplainSection = (section) => {
    requestExplanation(section);
  };

  // Handler for adding new section
  const handleAddSection = async (newSectionData) => {
    try {
      // Generate section format for display
      const newSection = {
        id: newSectionData.title.toLowerCase().replace(/[^a-z0-9]+/g, '-'),
        title: newSectionData.title,
        content: newSectionData.content,
        rawContent: newSectionData.content,
        category: newSectionData.category || parsedSections[parsedSections.length - 1]?.category || 'Pipeline Stage',
        isFirstInCategory: false
      };

      // Add to sections array
      const updatedSections = [...parsedSections, newSection];

      // Recalculate isFirstInCategory flags
      let lastSeenCategory = null;
      updatedSections.forEach(section => {
        if (section.category && section.category !== lastSeenCategory) {
          section.isFirstInCategory = true;
          lastSeenCategory = section.category;
        } else {
          section.isFirstInCategory = false;
        }
      });

      // Set the flag to prevent fetch from overwriting local edit
      markLocalEdit();
      isLocalUpdateRef.current = true;

      setParsedSections(updatedSections);

      // Reconstruct full markdown with new section
      let updatedContent = '';

      updatedSections.forEach((section, idx) => {
        if (idx > 0) updatedContent += '\n\n';

        // Add category header if this is the first section in the category
        if (section.isFirstInCategory && section.category) {
          updatedContent += `## ${section.category}\n\n`;
        }
        // Add section header (H3)
        updatedContent += `### ${section.title}\n\n${section.content}`;
      });

      // Update content ref
      contentRef.current = updatedContent.trim();

      // Update the current version
      if (updateCurrentVersion && currentVersion) {
        updateCurrentVersion({
          ...currentVersion,
          content: updatedContent.trim(),
          sections: updatedSections,
          parsedContent: updatedSections
        });
      }

      // Save to backend
      const updatedMessages = [...conversation.messages];

      // Find and update the specific version message
      let messageUpdated = false;
      if (currentVersion && currentVersion.id) {
        for (let i = 0; i < updatedMessages.length; i++) {
          if (updatedMessages[i]._id === currentVersion.id || updatedMessages[i].id === currentVersion.id) {
            updatedMessages[i] = {
              ...updatedMessages[i],
              content: updatedContent.trim()
            };
            messageUpdated = true;
            break;
          }
        }
      }

      // Fallback: update last assistant message
      if (!messageUpdated) {
        for (let i = updatedMessages.length - 1; i >= 0; i--) {
          if (updatedMessages[i].role === 'assistant') {
            updatedMessages[i] = {
              ...updatedMessages[i],
              content: updatedContent.trim()
            };
            break;
          }
        }
      }

      // Save to backend
      const conversationData = {
        messages: updatedMessages.map(msg => ({
          role: msg.role,
          content: msg.content,
          attachments: msg.attachments || [],
          createdAt: msg.createdAt || new Date().toISOString()
        }))
      };

      await conversationService.saveConversation(conversation._id, conversationData);

      // Notify parent component to refresh conversation (updates left panel)
      if (onConversationUpdate) {
        await onConversationUpdate(conversation._id);
      }

      showNotification({
        message: `"${newSection.title}" added successfully`,
        type: 'success'
      });

      setShowAddSectionModal(false);
      return true;
    } catch (error) {
      console.error('Error adding section:', error);
      showNotification({
        message: 'Failed to add new section',
        type: 'error'
      });
      return false;
    }
  };

  // Use content actions hook
  const {
    isExporting,
    isSaving: isSavingDocument,
    handleExport,
    handleCopy,
    handleRefreshDiagrams,
    handleSaveToDocument,
    getExportContent,
  } = useContentActions(contentRef, 'CI/CD Pipeline', projectId, conversation?._id);

  // Full screen functionality
  const {
    isFullScreen,
    toggleFullScreen,
    getLeftPanelStyles,
    getRightPanelStyles,
    getContentStyles
  } = useFullScreen();

  // Explain functionality
  const {
    isExplaining,
    explanationContent,
    showExplanationModal,
    currentSection: explainSection,
    requestExplanation,
    closeExplanationModal
  } = useExplain(projectId);

  // Improve functionality
  const {
    isImproving,
    improvedContent,
    showImprovementModal,
    currentSection: improveSection,
    showImproveInput,
    improvePrompt,
    setImprovePrompt,
    handleImproveClick,
    handleSendImprove,
    handleCancelImprove,
    closeImprovementModal,
    isSpecificSectionImprovement,
    specificSectionName
  } = useImprove(projectId);

  // Handler for applying improved content
  const handleUpdateImprovedContent = async () => {
    if (!improveSection || !improvedContent) return;
    try {
      if (improveSection.action === 'remove') {
        
      // Persist to ParsedResponse so changes survive refresh
      try {
        const ts = conversation?.messages?.find(m => m.role === 'assistant')?.createdAt;
        if (ts) {
          const conversationService = (await import('../../services/conversationService')).default;
          await conversationService.updateParsedSections(
            conversation._id || conversation.id,
            ts,
            [] // empty — backend will reconstruct from the saved conversation message
          );
        }
      } catch (e) { console.warn('ParsedResponse save failed:', e); }

      closeImprovementModal();
        showNotification({ message: `Removed: ${improveSection.title}`, type: 'success' });
        if (typeof onConversationUpdate === 'function') onConversationUpdate();
        return;
      }
      closeImprovementModal();
      showNotification({ message: 'Section improved successfully', type: 'success' });
    } catch (error) {
      showNotification({ message: 'Failed to apply improvement', type: 'error' });
    }
  };

  // Initialize validation state for sections with version-specific IDs
  const initialValidationState = parsedSections.reduce((acc, section) => {
    // Create version-specific validation ID
    const versionSpecificId = `${section.id}_vt${currentVersion?.timestamp || currentVersionIndex}`;
    acc[versionSpecificId] = false;
    return acc;
  }, {});

  // Use the validation hook with version-specific IDs
  const {
    validatedItems: validatedSections,
    handleValidateItem: handleValidateSectionBase,
    isLoading: isLoadingValidations,
    currentUserMemberType,
    isAssignedValidator,
    getValidatorType,
  } = useValidation({
    conversationId: conversation?._id,
    projectId,
    taskId: 'cicd-pipeline',
    initialState: initialValidationState
  });

  // Section content refs for text selection
  const sectionContentRefs = React.useRef({});
  const getSectionContentRef = (sectionId) => {
    if (!sectionContentRefs.current[sectionId]) {
      sectionContentRefs.current[sectionId] = { current: null };
    }
    return sectionContentRefs.current[sectionId];
  };

  // Inline comments
  const {
    commentCounts,
    sectionComments,
    openSectionId: commentOpenSectionId,
    openCommentPanel,
    closeCommentPanel,
    addComment,
    replyToComment,
    resolveComment: resolveCommentAction,
    reopenComment: reopenCommentAction,
    deleteComment: deleteCommentAction,
    isLoading: isCommentsLoading,
  } = useInlineComments({
    projectId,
    conversationId: conversation?._id,
    taskId: 'cicd-pipeline',
  });

  // Wrapper function to handle version-specific validation
  const handleValidateSection = async (sectionId) => {
    // Create version-specific ID
    const versionSpecificId = `${sectionId}_vt${currentVersion?.timestamp || currentVersionIndex}`;
    return handleValidateSectionBase(versionSpecificId);
  };

  console.log('SplitViewCICDPipeline rendering', {
    conversationId: conversation?._id,
    taskId: conversation?.taskId,
    isCICDPipelineConversation,
    isStreaming
  });


  // Auto-scroll panel to bottom when streaming content updates
  useEffect(() => {
    if (isStreaming && pipelinePanelRef.current && isCICDPipelineConversation) {
      setTimeout(() => {
        if (pipelinePanelRef.current) {
          pipelinePanelRef.current.scrollTop = pipelinePanelRef.current.scrollHeight;
        }
      }, 50);
    }
  }, [isStreaming, isCICDPipelineConversation]);



  // Don't show the split view if this isn't a CI/CD pipeline conversation or if there's no content

  if (!isCICDPipelineConversation || (!hasContent && !isStreaming)) {
    console.log('Not showing split view: either not CI/CD pipeline conversation or no content available');
    return <>{children}</>;
  }

  console.log('Showing split view with CI/CD pipeline content', { hasContent, isStreaming });

  // Render the split view
  return (
    <Box sx={{
      position: 'relative',
      width: '100%',
      height: '100%',
      mt: 5
    }}>
      {/* Main content with conditional split */}
      <Box sx={{
        display: 'flex',
        width: '100%',
        height: '100%',
        flexDirection: { xs: 'column', md: 'row' },
        flex: 1,
        overflow: 'hidden'
      }}>
        {/* Main conversation panel */}
        <Box sx={{
          ...getLeftPanelStyles(sidebarCollapsed),
          p: 2,
        }}>
          {children}
        </Box>

        {/* CI/CD Pipeline panel */}
        <Box
          ref={pipelinePanelRef}
          sx={{
            ...getRightPanelStyles(sidebarCollapsed),
            backgroundColor: '#f7f7f7',
            height: '97%',
            p: 2
          }}
        >
          <Paper
            elevation={0}
            sx={{
              ...getContentStyles({}),
              borderRadius: "8px",
              backgroundColor: 'white',
              height: '100%',
              display: 'flex',
              flexDirection: 'column',
              boxShadow: '0 1px 3px rgba(0,0,0,0.1)',
            }}
          >
            <Box sx={{
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'space-between',
              p: 3,
              pb: 2,
              backgroundColor: 'white',
              position: 'sticky',
              top: 0,
              zIndex: 10,
            }}>
              <SectionHeader label='CI/CD Pipeline' />
              <Box sx={{ display: 'flex', alignItems: 'center', gap: 2 }}>
                {/* Version selector */}
                {hasVersions && versions.length > 1 && (
                  <VersionSelector
                    versions={versions}
                    currentVersionIndex={currentVersionIndex}
                    onVersionChange={setCurrentVersionIndex}
                    disabled={isStreaming}
                  />
                )}
                {/* Fixed header section */}
                <TopBarButtons
                  onAdd={() => setShowAddSectionModal(true)}
                  addTooltip="Add new pipeline stage"
                  isFullScreen={isFullScreen}
                  onToggleFullScreen={toggleFullScreen}
                  getExportContent={getExportContent}
                  onExport={handleExport}
                  isExporting={isExporting}
                  onCopy={handleCopy}
                  onRefresh={handleRefreshDiagrams}
                  onSave={projectId ? () => handleSaveToDocument('CICD_PIPELINE') : undefined}
                  isSaving={isSaving || isSavingDocument}
                  disabled={!contentRef.current}
                  exportTooltip="Export CI/CD pipeline as markdown"
                  copyTooltip="Copy CI/CD pipeline to clipboard"
                  refreshTooltip="Refresh diagrams"
                  saveTooltip="Save as document"
                />
              </Box>
            </Box>


            {/* Task-level approval tracking */}
            <TaskApprovalBar
              projectId={projectId}
              taskId={'cicd-pipeline'}
              conversationId={conversation?._id || conversation?.id}
              validatedItems={validatedSections}
              totalItems={parsedSections?.length || 0}
                currentUserMemberType={currentUserMemberType}
            />
            <Box sx={{
              p: 3,
              pt: 2,
              flex: 1,
              overflow: "auto",
              '&::-webkit-scrollbar': {
                width: '8px',
                backgroundColor: 'transparent',
              },
              '&::-webkit-scrollbar-track': {
                borderRadius: '10px',
                backgroundColor: 'rgba(0, 0, 0, 0.05)',
                marginTop: '5px',
                marginBottom: '5px',
              },
              '&::-webkit-scrollbar-thumb': {
                borderRadius: '10px',
                backgroundColor: 'rgba(0, 0, 0, 0.2)',
                '&:hover': {
                  backgroundColor: 'rgba(25, 118, 210, 0.4)',
                },
              },
              '&::-webkit-scrollbar-corner': {
                backgroundColor: 'transparent',
              },
            }}>
              {/* Show CI/CD pipeline content or a placeholder if none found yet */}
              {isStreaming ? (
                <LoadingBox message='Generating CI/CD pipeline...' spinnerSize={40} />
              ) : parsedSections.length > 0 ? (
                <Box>
                  {/* Show loading banner when streaming with existing content */}
                  {isStreaming && (
                    <Box sx={{
                      mb: 3,
                      p: 2,
                      backgroundColor: '#e3f2fd',
                      borderRadius: 1,
                      display: 'flex',
                      alignItems: 'center',
                      justifyContent: 'center',
                      gap: 2,
                      border: '1px solid #90caf9'
                    }}>
                      <CircularProgress size={20} />
                      <Typography variant="body2" color="primary" sx={{ fontWeight: 500 }}>
                        Generating CI/CD pipeline content...
                      </Typography>
                    </Box>
                  )}
                  {/* Render sections grouped by category */}
                {(() => {
                  const groups = groupedSections;
                  let globalIndex = 0;
                  return groups.slice(0, visibleSections).map((group, groupIndex) => (
                    <Box key={group.categoryId} sx={{ mb: 3 }}>
                      {group.category && (
                        <Box sx={{ mt: groupIndex === 0 ? 0 : 2, mb: 1.5 }}>
                          <SectionHeader label={group.category} />
                        </Box>
                      )}
                      {group.categorySection && (
                        <Box sx={{ pl: 2, mb: 2 }}>
                          <Box sx={{ p: 2, backgroundColor: 'grey.50', borderRadius: 1, border: '1px solid', borderColor: 'grey.200' }}>
                            <MarkdownRenderer content={group.categorySection.content || ''} />
                          </Box>
                        </Box>
                      )}
                      <Box sx={{ pl: group.category ? 2 : 0 }}>
                        {group.sections.map((section) => {
                          const idx = globalIndex++;
                          const sectionId = section.id;
                          const versionSpecificId = `${sectionId}_vt${currentVersion?.timestamp || currentVersionIndex}`;
                          const isValidated = validatedSections[versionSpecificId] || false;
                          return (<React.Fragment key={section.id}>
<Accordion
                        ref={shouldHighlight(section.id) ? highlightRef : null}
                        title={section.title}
                        expanded={expandedSections[section.id] || false}
                        onChange={() => handleSectionToggle(section.id)}
                        expandIcon={true}
                        titleContent={editingSection === section.id ? (
                          <Box title="" onClick={(e) => e.stopPropagation()} sx={{ maxWidth: '93%', width: '93%' }}>
                            <TextField
                              value={editedTitle}
                              onChange={(e) => setEditedTitle(e.target.value)}
                              onKeyDown={(e) => e.stopPropagation()}
                              onFocus={(e) => e.target.removeAttribute('title')}
                              size="small"
                              fullWidth
                              placeholder="Enter pipeline stage title"
                              variant="outlined"
                              InputProps={{ title: '' }}
                              inputProps={{ title: '', autoComplete: 'off' }}
                              sx={{
                                '& .MuiOutlinedInput-root': {
                                  fontSize: '14px',
                                  fontFamily: 'inherit',
                                  backgroundColor: '#fff',
                                  '& fieldset': { borderWidth: '1px' },
                                  '&:hover fieldset': { borderWidth: '1px' },
                                  '&.Mui-focused fieldset': { borderWidth: '1px' },
                                },
                                '& .MuiOutlinedInput-input': {
                                  fontSize: '14px',
                                  fontFamily: 'inherit',
                                  padding: '8px 12px',
                                },
                              }}
                            />
                          </Box>
                        ) : null}
                        rightIcon={
                        <Box
                          onClick={async (e) => {
                            e.stopPropagation();

                            try {
                              await handleValidateSection(section.id);
                            } catch (error) {
                              showNotification({
                                message: `Failed to update validation for "${section.title}"`,
                                type: "error"
                              });
                            }
                          }}
                          sx={{ display: 'flex', alignItems: 'center', cursor: isAssignedValidator ? 'pointer' : 'default', opacity: isAssignedValidator ? 1 : 0.4, pointerEvents: isAssignedValidator ? 'auto' : 'none' }}
                        >
                          {isValidated ? (
                            <ValidatedIcon size={25} memberType={getValidatorType(versionSpecificId)} />
                          ) : (
                            <UnvalidatedIcon size={25} />
                          )}
                        </Box>
                      }
                      sx={{
                        mb: 3,
                        '&.Mui-expanded': {
                          margin: 0,
                        }
                      }}
                    >
                      <ScrollableBox
                        minHeight="300px"
                        sx={{
                            textAlign: 'justify',
                            cursor: editingSection === section.id ? 'default' : 'auto',
                          }}
                      >
                        {/* Show edit mode or content */}
                        {editingSection === section.id ? (
                          // Edit mode
                          <EditableTextField
                            value={editedContent}
                            onChange={(e) => setEditedContent(e.target.value)}
                          />
                        ) : (
                          // Normal content display
                          <Box
                            ref={getSectionContentRef(section.id)}
                            sx={{ position: "relative" }}
                          >
                            <MarkdownWithMermaid content={section.content} zoomLevel={zoomLevel} />
                            <TextSelectionPopup
                            containerRef={getSectionContentRef(section.id)}
                            sectionId={section.id}
                            onSubmitComment={addComment}
                            disabled={editingSection === section.id}

                          />
                          </Box>
                        )}
                      </ScrollableBox>
                      {/* Action buttons */}
                      <Box sx={{
                        position: 'absolute',
                        right: 10,
                        top: 10,
                        display: 'flex',
                        flexDirection: 'column',
                        gap: 0.5
                      }}>
                        <ActionButtons
                          item={section}
                          itemIndex={idx}
                          featureId={section.id}
                          editingFeature={editingSection}
                          isSaving={isSaving}
                          onEdit={() => handleStartEdit(section.id, section.content || '', section.title || '')}
                          onSave={() => handleSaveEdit(section.id)}
                          onCancel={handleCancelEdit}
                          onImprove={() => handleImproveClick(section)}
                          onExplain={() => handleExplainSection(section)}
                          onCopy={() => handleCopySection(section)}
                          onDelete={() => handleDeleteSection(section)}
                          onZoomIn={handleZoomIn}
                          onZoomOut={handleZoomOut}
                          showZoomControls={hasMermaidDiagram(section.content || section.rawContent || '')}
                          zoomLevel={zoomLevel}
                          minZoom={0.25}
                          maxZoom={2.0}
                          vertical={true}
                          onComment={() => openCommentPanel(section.id)}
                          commentCount={commentCounts[section.id]?.open || 0}
                          isApproved={isValidated}
                          approvedByType={isValidated ? getValidatorType(versionSpecificId) : null}
                        />
                      </Box>
                    </Accordion>
                    {showImproveInput === section.id && (
                      <InlineImproveInput
                        improvePrompt={improvePrompt}
                        onPromptChange={setImprovePrompt}
                        onSend={() => handleSendImprove(section)}
                        onCancel={handleCancelImprove}
                      />
                    )}
                    </React.Fragment>);
                        })}
                      </Box>
                    </Box>
                  ));
                })()}

                {/* Load more button */}
                {(() => {
                  const groups = groupedSections;
                  return groups.length > visibleSections && (
                    <LoadMoreButton
                      remaining={groups.length - visibleSections}
                      onLoadMore={handleLoadMore}
                      onShowAll={handleShowAll}
                      label="Load more"
                    />
                  );
                })()}
                </Box>
              ) : (
                <EmptyContentPlaceholder
                  title="No CI/CD pipeline content found yet."
                  description="Ask the AI to create a comprehensive CI/CD pipeline for your project. You can use prompts like:"
                  examplePrompt="Can you create a complete CI/CD pipeline with stages for build, test, security scanning, and deployment?"
                  additionalInfo="You can also ask for specific pipeline configurations, Docker setups, deployment strategies, or integration with tools like Jenkins, GitHub Actions, or GitLab CI."
                />
              )}
            </Box>
          </Paper>
        </Box>
      </Box>

      {/* Confirmation dialog for delete */}
      <ConfirmationDialog
        open={deleteConfirmation.open}
        onClose={() => setDeleteConfirmation({ open: false, section: null })}
        onConfirm={handleDeleteSectionConfirmed}
        title="Delete Section"
        message={`Are you sure you want to delete "${deleteConfirmation.section?.title}"? This action cannot be undone.`}
        confirmText="Delete"
        cancelText="Cancel"
        severity="warning"
        height='auto'
      />

      {/* Explanation Modal */}
      <ExplanationModal
        open={showExplanationModal}
        onClose={closeExplanationModal}
        title={explainSection?.title || ''}
        explanation={explanationContent}
        isLoading={isExplaining}
      />

      {/* Add Section Modal */}
      <AddSectionModal
        open={showAddSectionModal}
        onClose={() => setShowAddSectionModal(false)}
        onSave={handleAddSection}
        sectionType="pipeline-stage"
        existingSections={parsedSections}
      />

      {/* Improvement Modal */}
      <ImprovementModal
        open={showImprovementModal}
        onClose={closeImprovementModal}
        section={improveSection}
        improvedContent={improvedContent}
        isGenerating={isImproving}
        onUpdate={handleUpdateImprovedContent}
        isSpecificSection={isSpecificSectionImprovement}
        specificSectionName={specificSectionName}
      />
    

      {/* Inline Comment Thread Panel */}
      <CommentThreadPanel
        open={!!commentOpenSectionId}
        onClose={closeCommentPanel}
        sectionTitle={
          (parsedSections || []).find(s => s.id === commentOpenSectionId)?.title || commentOpenSectionId
        }
        comments={sectionComments}
        isLoading={isCommentsLoading}
        onAddComment={(content) => addComment({ sectionId: commentOpenSectionId, content })}
        onReply={replyToComment}
        onResolve={resolveCommentAction}
        onReopen={reopenCommentAction}
        onDelete={deleteCommentAction}
      />
</Box>
  );
};

export default SplitViewCICDPipeline;
